import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ContentItem, CreateContentInput } from '../lib/api'
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Send,
  Clock,
  FileText
} from 'lucide-react'
import { useForm } from 'react-hook-form'

export function Content() {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const queryClient = useQueryClient()

  const { data: content, isLoading } = useQuery({
    queryKey: ['content'],
    queryFn: () => api.getContent(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteContent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] })
    },
  })

  const filteredContent = content?.filter(item =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.body.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Content
        </button>
      </div>

      {/* Content List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="card animate-pulse h-24 bg-slate-100" />
          ))}
        </div>
      ) : filteredContent && filteredContent.length > 0 ? (
        <div className="space-y-4">
          {filteredContent.map((item) => (
            <ContentCard
              key={item.id}
              item={item}
              onDelete={() => deleteMutation.mutate(item.id)}
            />
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No content yet</h3>
          <p className="text-slate-500 mb-4">Create your first piece of content to get started.</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            Create Content
          </button>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateContentModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  )
}

function ContentCard({
  item,
  onDelete
}: {
  item: ContentItem
  onDelete: () => void
}) {
  const [showMenu, setShowMenu] = useState(false)

  const statusColors = {
    draft: 'badge-info',
    scheduled: 'badge-warning',
    published: 'badge-success',
    archived: 'bg-slate-100 text-slate-600',
  }

  const platformIcons: Record<string, string> = {
    twitter: '𝕏',
    reddit: '🔴',
    linkedin: '💼',
    tiktok: '🎬',
    blog: '📝',
  }

  return (
    <div className="card hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">{platformIcons[item.platform] || '📱'}</span>
            <h3 className="font-medium text-slate-900 truncate">{item.title}</h3>
            <span className={statusColors[item.status]}>{item.status}</span>
          </div>
          <p className="text-sm text-slate-600 line-clamp-2 mb-3">{item.body}</p>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(item.createdAt).toLocaleDateString()}
            </span>
            {item.scheduledAt && (
              <span className="flex items-center gap-1">
                Scheduled: {new Date(item.scheduledAt).toLocaleString()}
              </span>
            )}
          </div>
        </div>

        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <MoreVertical className="w-4 h-4" />
          </button>

          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMenu(false)}
              />
              <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-slate-200 z-20">
                <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <Send className="w-4 h-4" />
                  Publish
                </button>
                <button
                  onClick={() => {
                    setShowMenu(false)
                    onDelete()
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function CreateContentModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const { register, handleSubmit, formState: { errors } } = useForm<CreateContentInput>()

  const createMutation = useMutation({
    mutationFn: (data: CreateContentInput) => api.createContent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] })
      onClose()
    },
  })

  const onSubmit = (data: CreateContentInput) => {
    createMutation.mutate(data)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">Create Content</h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Title
            </label>
            <input
              {...register('title', { required: 'Title is required' })}
              className="input"
              placeholder="Enter content title"
            />
            {errors.title && (
              <p className="text-sm text-red-600 mt-1">{errors.title.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Platform
            </label>
            <select
              {...register('platform', { required: 'Platform is required' })}
              className="input"
            >
              <option value="">Select platform</option>
              <option value="twitter">Twitter/X</option>
              <option value="linkedin">LinkedIn</option>
              <option value="reddit">Reddit</option>
              <option value="tiktok">TikTok</option>
              <option value="blog">Blog</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Content
            </label>
            <textarea
              {...register('body', { required: 'Content is required' })}
              className="input min-h-[150px]"
              placeholder="Write your content..."
            />
            {errors.body && (
              <p className="text-sm text-red-600 mt-1">{errors.body.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="btn-primary"
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
